import { GroupSecurity } from '@quromedical/fhir-common';
import type {
  CreateQualificationFields,
  External,
  PersonContact,
  PractitionerGeneral,
  Settings,
} from '@quromedical/models';
import { PractitionerActiveToggleButton } from 'core/practitioner';
import {
  Alert,
  Section,
  Skeleton,
  SkeletonProvider,
  Snackbar,
} from 'design-system';
import { useLocalSearchParams } from 'expo-router';
import { onMapsPress } from 'helpers/platform-links';
import { useRevalidation } from 'hooks/useRevalidation';
import { useUserSession } from 'hooks/useUserSession';
import {
  getPlaceAddressPartialFetcher,
  googleSearchAddressFetcher,
} from 'integration';
import { PractitionerApi } from 'integration/aggregate';
import type { PractitionerStackScreenProps } from 'navigation';
import type React from 'react';
import { useCallback } from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { PersonContactForm, PersonGeneralForm } from 'screens/person';
import { strings } from 'strings';
import useSWR from 'swr';
import { trpcClient } from 'trpc';

import {
  PractitionerExternalCard,
  PractitionerGroupMembershipCard,
  PractitionerPatientsCard,
  PractitionerQualificationCard,
  PractitionerSettingsCard,
} from './card';

const api = new PractitionerApi();

type RouteParams = {
  id: string;
};

export const PractitionerScreen: React.FC<
  PractitionerStackScreenProps<'View'>
> = () => {
  const { id } = useLocalSearchParams<RouteParams>();

  const session = useUserSession();

  const canEdit = session.hasAny([GroupSecurity.PractitionerUpdate]);

  const fetcher = useCallback(
    () => trpcClient.practitioner.getById.query({ id }),
    [id],
  );

  const swrKey = `Practitioner/${id}`;
  const { data, isValidating, error } = useSWR(swrKey, fetcher);
  const revalidate = useRevalidation(swrKey);
  const onGeneralSubmit = useCallback(
    async (formData: PractitionerGeneral) => {
      await api.updatePractitionerGeneral({ ...formData, id });
    },
    [id],
  );

  const onContactSubmit = useCallback(
    async (results: PersonContact) => {
      await api.updatePractitionerContact({
        ...results,
        id,
      });
    },
    [id],
  );

  const onQualificationSubmit = useCallback(
    async (results: CreateQualificationFields) => {
      await api.updatePractitionerQualification({ ...results, id });
    },
    [id],
  );

  const onSettingsSubmit = useCallback(
    async (results: Settings) => {
      await api.updatePractitionerSettings({ ...results, id });
    },
    [id],
  );

  const onExternalSubmit = useCallback(
    async (results: External) => {
      await api.updatePractitionerExternal({ ...results, id });
    },
    [id],
  );

  const onMapsPressed = useCallback(() => {
    onMapsPress(data?.contact);
  }, [data?.contact]);

  if (error) {
    return (
      <Snackbar onClose={revalidate} isOpen={!!error}>
        <Alert
          backgroundColor="status-critical"
          textColor="white"
          onAction={revalidate}
          actionIcon="close"
          title={strings.ErrorCardTitle}
          body={strings.GenericErrorMessage}
        />
      </Snackbar>
    );
  }

  const loading = isValidating || !data;

  const isActive = !!data?.active;

  const isButtonDisabled = !data?.contact?.addressCoordinates;

  return (
    <ScrollView>
      <SkeletonProvider loading={loading}>
        <Section>
          <Skeleton>
            <PersonGeneralForm
              canEdit={canEdit}
              revalidate={revalidate}
              data={data?.general}
              onSubmit={onGeneralSubmit}
              minimalData={true}
            />
          </Skeleton>
        </Section>

        <Section>
          <Skeleton>
            <PersonContactForm
              canEdit={canEdit}
              data={data?.contact}
              onSubmit={onContactSubmit}
              revalidate={revalidate}
              hasEmergencyAccess={false}
              addressFetcher={googleSearchAddressFetcher}
              getAddressPartial={getPlaceAddressPartialFetcher}
              onMapsPress={onMapsPressed}
              isButtonDisabled={isButtonDisabled}
            />
          </Skeleton>
        </Section>
        <Section unsetZIndex>
          <Skeleton>
            <PractitionerQualificationCard
              onSubmit={onQualificationSubmit}
              data={data?.qualification}
              canEdit={canEdit}
              revalidate={revalidate}
            />
          </Skeleton>
        </Section>
        <Section>
          <Skeleton>
            <PractitionerSettingsCard
              onSubmit={onSettingsSubmit}
              data={data?.settings}
              canEdit={canEdit}
              revalidate={revalidate}
            />
          </Skeleton>
        </Section>
        <Section>
          <Skeleton>
            <PractitionerExternalCard
              onSubmit={onExternalSubmit}
              data={data?.external}
              canEdit={canEdit}
              revalidate={revalidate}
            />
          </Skeleton>
        </Section>
        <Section>
          <Skeleton>
            <PractitionerGroupMembershipCard data={data?.groups} />
          </Skeleton>
        </Section>
        <Section>
          <Skeleton>
            <PractitionerPatientsCard data={data?.assignedPatients} />
          </Skeleton>
        </Section>

        <Section alignItems="flex-end" hasBottomMargin>
          <Skeleton>
            <PractitionerActiveToggleButton
              id={id}
              isActive={isActive}
              canEdit={canEdit}
            />
          </Skeleton>
        </Section>
      </SkeletonProvider>
    </ScrollView>
  );
};
